<template>
  <v-card
    class="pa-6"
  >
    <v-card-title class="justify-space-between pa-0">
      <div :class="`${titleColor}--text subtitle-1 font-weight-medium`">
        <span>{{ scope === 'list' ? 'Bookmarks' : 'Bookmark' }}</span>
      </div>

      <v-card-actions>
        <v-btn
          class="px-4"
          :color="titleColor"
          outlined
          small
          transition="scroll-y-transition"
          @click.stop="cancelForm"
        >
          <v-icon x-small>
            close
          </v-icon>

          <span
            class="caption pl-1"
            small
          >
            Cancel
          </span>
        </v-btn>
      </v-card-actions>
    </v-card-title>

    <v-card-text class="pa-0">
      <v-col class="pa-0">
        <v-form
          ref="refBookmarkForm"
          v-model="isValidBookmarkForm"
        >
          <v-container class="pa-0">
            <v-col>
              <draggable
                v-model="entry"
                data-role="draggable"
              >
                <v-row
                  v-for="(bookmark, bookmarkIndex) in entriesToEdit"
                  :key="bookmarkIndex"
                  :class="`${bookmarkIndex === 0 ? 'mt-0' : 'mt-4'}`"
                  :style="`${inDarkMode ? 'background: #212121; border: 1px solid #424242;' : 'border: 1px solid #e0e0e0;'}`"
                  transition="scroll-y-transition"
                >
                  <v-col>
                    <v-row
                      :class="`pl-0 py-0 align-center`"
                    >
                      <v-col
                        v-if="isEditingMany"
                        class="col-auto px-0"
                      >
                        <v-btn
                          class="pa-4 cursor-grab"
                          color="coolgray"
                          dark
                          icon
                          x-small
                          transition="scroll-y-transition"
                          title="Drag to Reorder"
                        >
                          <v-icon>
                            drag_indicator
                          </v-icon>
                        </v-btn>
                      </v-col>

                      <v-col
                        :class="`col-grow pt-0 ${isEditingMany ? 'pl-0' : 'px-4'}`"
                      >
                        <search-simple
                          event-name="tools/editBookmark"
                          :input-label="bookmark.reportLabel"
                          :selected-item="bookmark"
                          :show-all-results="false"
                          text-key="textAlt"
                          value-key="href"
                        />
                      </v-col>

                      <v-col
                        v-if="isEditingMany"
                        class="col-auto"
                      >
                        <v-row>
                          <v-btn
                            class="pa-4 mr-2"
                            dark
                            icon
                            x-small
                            transition="scroll-y-transition"
                            @click.stop="removeBookmark(bookmarkIndex)"
                          >
                            <v-icon
                              color="toasted"
                            >
                              close
                            </v-icon>
                          </v-btn>
                        </v-row>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </draggable>

              <v-card-actions class="row mt-4 px-0">
                <v-col class="d-flex justify-space-between px-0">
                  <v-btn
                    v-if="isEditingItem"
                    class="px-4"
                    color="toasted"
                    transition="scroll-y-transition"
                    outlined
                    @click.stop="removeBookmark(entriesToEdit[0].index)"
                  >
                    <span class="caption pl-1">
                      Delete
                    </span>
                  </v-btn>

                  <v-btn
                    v-else
                    class="px-4"
                    :color="titleColor"
                    transition="scroll-y-transition"
                    outlined
                    @click.stop="pushBookmark"
                  >
                    <span class="caption pl-1">
                      Add
                    </span>
                  </v-btn>

                  <v-btn
                    class="px-4"
                    color="blue"
                    dark
                    transition="scroll-y-transition"
                    @click.stop="submitBookmarkForm('Saved')"
                  >
                    <v-icon x-small>
                      save
                    </v-icon>

                    <span class="caption pl-1">
                      Save
                    </span>
                  </v-btn>
                </v-col>
              </v-card-actions>
            </v-col>
          </v-container>
        </v-form>
      </v-col>
    </v-card-text>
  </v-card>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import draggable from 'vuedraggable'

import BookmarkMixin from 'src/tools/store/utils/bookmarks-mixin'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapGetters: mapLayoutGetters } = createNamespacedHelpers('layout')

export default {
  name: 'BookmarkForm',
  components: {
    draggable,
    SearchSimple: () => import('src/tools/components/search/SearchSimple'),
  },
  mixins: [
    BookmarkMixin,
  ],
  computed: {
    ...mapAuthGetters([
      'inDarkMode',
    ]),
    ...mapLayoutGetters([
      'titleColor',
    ]),
    isEditingItem() {
      return this.entriesToEdit.length === 1 && this.scope === 'item'
    },
    isEditingMany() {
      return this.entriesToEdit.length > 1
    },
    hasFormChanged() {
      return false // this.entriesToEdit && (this.entriesToEdit.find(this.diffEntry) || this.entry.length !== this.bookmarks.length)
    },
    rulesRequired() {
      return (bookmark) => {
        const { reportPath, reportCode, baseReportName } = bookmark
        return [
          (value) => {
            const isStillEditing = !reportPath && !reportCode && !baseReportName
            const isValidValue = isStillEditing || !!value || [0, false].indexOf(value) !== -1
            return isValidValue || 'Required'
          },
        ]
      }
    },
  },
  methods: {
    diffEntry(entry, entryIndex) {
      const oldEntry = this.bookmarks.find((originalEntry, originalEntryIndex) => originalEntryIndex === entryIndex)

      const diffValues = () => {
        const diffMap = [
          () => oldEntry.billingCode !== entry.billingCode,
          () => oldEntry.reportPath !== entry.reportPath,
          () => oldEntry.reportCode !== entry.reportCode,
          () => oldEntry.baseReportName !== entry.baseReportName,
          () => oldEntry.routeName !== entry.routeName,
          () => oldEntry.teamId !== entry.teamId,
        ]

        return diffMap.reduce((diffResult, diff) => {
          if (diffResult !== true && diff()) {
            diffResult = true
          }

          return diffResult
        }, false)
      }

      return !oldEntry || diffValues()
    },
  },
}
</script>
